import { appEnvironment, appName, runningClientSide } from '@benzinga/utils';
import { Targeting } from './entities';

declare global {
  interface Window {
    adthrive: {
      cmd?: unknown[];
      plugin?: string;
      host?: string;
      disableAds?: () => void;
    };
    raptivetarget: Targeting;
  }
}

const btLoaderScriptId = 'bt-loader-script';
const raptiveFooterScriptId = 'raptive-footer-script';
const raptiveInitScriptId = 'raptive-init-script';

export const injectRaptiveScript = ({
  onError,
  onLoad,
  pageTargeting,
}: {
  onError?: () => void;
  onLoad?: () => void;
  pageTargeting?: Targeting;
} = {}) => {
  const isBzApp = appEnvironment().isApp(appName.bz);
  const isMoneyApp = appEnvironment().isApp(appName.money);
  if (!runningClientSide() || (!isBzApp && !isMoneyApp)) return;

  removeRaptiveScript();

  window.raptivetarget = { ...(window.raptivetarget || {}), ...(pageTargeting || {}) };

  window.adthrive = window.adthrive || {};
  window.adthrive.cmd = window.adthrive.cmd || [];
  window.adthrive.plugin = 'adthrive-ads-manual';
  window.adthrive.host = 'ads.adthrive.com';

  const src =
    'https://' +
    window.adthrive.host +
    '/sites/6633ac5da223395e1aa0358b/ads.min.js?referrer=' +
    window.encodeURIComponent(window.location.origin + window.location.pathname) +
    '&cb=' +
    (Math.floor(Math.random() * 100) + 1);

  const scriptElement = document.createElement('script');
  scriptElement.src = src;
  scriptElement.setAttribute('async', 'true');
  scriptElement.setAttribute('id', raptiveInitScriptId);
  if (typeof onLoad === 'function') {
    scriptElement.onload = onLoad;
  }
  scriptElement.onerror = (event: Event | string) => {
    if (typeof event === 'object' && event?.type === 'error') {
      console.error('Failed to inject Raptive script');
    }
    typeof onError === 'function' && onError();
  };

  document.body.appendChild(scriptElement);
};

export const removeRaptiveScript = () => {
  if (!runningClientSide()) return;
  const script = document.querySelector(`#${raptiveInitScriptId}`);
  if (script) {
    script.remove();
  }
};

export const appendAdLightClassToBody = () => {
  if (!runningClientSide()) return;
  document.body.classList.add('adlight');
};

export const appendPaywallClassToBody = () => {
  if (!runningClientSide()) return;
  document.body.classList.add('paywalled');
};

export const appendFooterDisabledClassToBody = () => {
  if (!runningClientSide()) return;
  document.body.classList.add('disableFooter');
};

export const removeFooterDisabledClassFromBody = () => {
  if (!runningClientSide()) return;
  document.body.classList.remove('disableFooter');
};

export const appendStagingClassToBody = () => {
  if (!runningClientSide()) return;
  const stagingSites = ['bz.zingbot.bz', 'wwwbeta.benzinga.com'];
  if (stagingSites.includes(window.location.hostname)) {
    document.body.classList.add('staging');
  }
};

export const appendSandboxClassToBody = () => {
  if (!runningClientSide()) return;
  if (window.location.href.includes('bzsbx.benzinga.com')) {
    document.body.classList.add('sandbox');
  }
};

export const isPaywallClassAppended = (): boolean => {
  if (!runningClientSide()) return false;
  return document.body.classList.contains('paywalled');
};

const injectBTLoaderScript = () => {
  if (!runningClientSide()) return;
  const script = document.createElement('script');
  script.src = 'https://btloader.com/tag?o=5698917485248512&upapi=true&domain=benzinga.com';
  script.id = btLoaderScriptId;
  script.async = true;
  document.body.appendChild(script);
};

const injectRaptiveFooterScript = () => {
  if (!runningClientSide()) return;
  const script = document.createElement('script');
  script.id = raptiveFooterScriptId;
  script.textContent = `
    !function(){
    "use strict";
    var e;e=document,function(){
      var t,n;function r(){
      var t=e.createElement("script");
      t.src="https://cafemedia-com.videoplayerhub.com/galleryplayer.js",e.head.appendChild(t)
      }
      function a(){var t=e.cookie.match("(^|[^;]+)s*__adblockers*=\\s*([^;]+)");return t&&t.pop()}function c(){clearInterval(n)}return{init:function(){var e;"true"===(t=a())?r():(e=0,n=setInterval((function(){100!==e&&"false" !== t || c(), "true" === t && (r(), c()), t = a(), e++}), 50))}}}().init()}();
  `;
  document.body.appendChild(script);
};

export const injectRaptiveFooterAdScript = () => {
  injectBTLoaderScript();
  injectRaptiveFooterScript();
};

export const removeRaptiveFooterAdScript = () => {
  if (!runningClientSide()) return;
  const btLoader = document.querySelector(`#${btLoaderScriptId}`);
  const raptiveFooter = document.querySelector(`#${raptiveFooterScriptId}`);
  if (btLoader) btLoader.remove();
  if (raptiveFooter) raptiveFooter.remove();
};

export const captureEmailFromPageUrl = () => {
  if (!runningClientSide()) return;

  const urlParams = window.location.search.substring(1).split('&');

  const removeSpaces = (text: string) => text.replace(/\s/g, '');

  const hashEmail = (email: string) =>
    new Promise<string>(resolve => {
      if (
        !('msCrypto' in window) &&
        'https:' === window.location.protocol &&
        'crypto' in window &&
        'TextEncoder' in window
      ) {
        const encodedEmail = new TextEncoder().encode(email);
        crypto.subtle.digest('SHA-256', encodedEmail).then(hashedArray => {
          const hashedEmail = Array.from(new Uint8Array(hashedArray))
            .map(byte => ('00' + byte.toString(16)).slice(-2))
            .join('');
          resolve(hashedEmail);
        });
      } else resolve('');
    });

  for (let i = 0; i < urlParams.length; i++) {
    const emailParamKey = 'adt_ei';
    const decodedParam = decodeURIComponent(urlParams[i]);

    if (decodedParam.startsWith(emailParamKey)) {
      const capturedEmail = decodedParam.split(emailParamKey + '=')[1];

      const isValidEmail = (email: string) => {
        const emailRegex = /([a-zA-Z0-9._!#$%+^&*()[\]<>-]+)@[a-zA-Z0-9._-]+\.[a-zA-Z0-9._-]+/gi;
        const matches = email.match(emailRegex);
        return matches ? matches[0] : '';
      };

      if (isValidEmail(removeSpaces(capturedEmail.toLowerCase()))) {
        hashEmail(capturedEmail).then(hashedEmail => {
          if (hashedEmail.length) {
            localStorage.setItem(emailParamKey, hashedEmail);
            localStorage.setItem('adt_emsrc', 'url');
            urlParams.splice(i, 1);
            window.history.replaceState(null, '', '?' + urlParams.join('&'));
          }
        });
        break;
      }
    }
  }
};

export { getReferrerPartnerName, getReferrerSiteName, getUtmSourceName } from '../referrerPartner';

export const detectAndRemoveEmailsFromUrl = async () => {
  if (!runningClientSide()) return;

  const checkEmail = (email: string): string => {
    const matches = email.match(/((?=([a-z0-9._!#$%+^&*()[\]<>-]+))\2@[a-z0-9._-]+\.[a-z0-9._-]+)/gi);
    return matches ? matches[0] : '';
  };

  const validateEmail = (email: string): string => {
    return checkEmail(trimInput(email.toLowerCase()));
  };

  const trimInput = (text: string): string => {
    return text.replace(/\s/g, '');
  };

  const hashEmail = async (email: string): Promise<{ sha1Hash: string; sha256Hash: string }> => {
    const result = { sha1Hash: '', sha256Hash: '' };

    if (
      !('msCrypto' in window) &&
      'https:' === window.location.protocol &&
      'crypto' in window &&
      'TextEncoder' in window
    ) {
      const encodedEmail = new TextEncoder().encode(email);
      const [sha256Hash, sha1Hash] = await Promise.all([hash('SHA-256', encodedEmail), hash('SHA-1', encodedEmail)]);
      result.sha256Hash = sha256Hash;
      result.sha1Hash = sha1Hash;
    }

    return result;
  };

  const hash = async (algorithm: string, data: Uint8Array): Promise<string> => {
    const buffer = new ArrayBuffer(data.length);
    new Uint8Array(buffer).set(data);
    const digest = await crypto.subtle.digest(algorithm, buffer);
    return Array.from(new Uint8Array(digest))
      .map(byte => ('00' + byte.toString(16)).slice(-2))
      .join('');
  };

  const hasHashes = (hashes: { sha1Hash: string; sha256Hash: string }): boolean => {
    let isValid = true;
    Object.keys(hashes).forEach(key => {
      if (hashes[key as keyof typeof hashes].length === 0) {
        isValid = false;
      }
    });
    return isValid;
  };

  const removeEmailAndReplaceHistory = (params: URLSearchParams, key: string, url: URL) => {
    params.delete(key);
    const newUrl = '?' + params.toString() + url.hash;
    window.history.replaceState(null, '', newUrl);
  };

  const url = new URL(window.location.href);
  const params = url.searchParams;

  let emailParam: { emsrc: string; key: string; value: string } | undefined;
  let hashedEmailParam: { emsrc: string; key: string; value: string } | undefined;

  const hashParams = ['adt_eih', 'sh_kit'];
  const emailParams = ['adt_ei', 'he'];

  for (const [key, value] of params.entries()) {
    if (emailParams.includes(key)) {
      emailParam = { emsrc: 'url', key, value };
    } else if (hashParams.includes(key)) {
      hashedEmailParam = {
        emsrc: key === 'sh_kit' ? 'urlhck' : 'urlh',
        key,
        value,
      };
    }
  }

  if (emailParam) {
    const validEmail = validateEmail(emailParam.value);
    if (validEmail) {
      const hashes = await hashEmail(validEmail);
      if (hasHashes(hashes)) {
        const emailData = {
          created: Date.now(),
          value: hashes,
        };
        localStorage.setItem('adt_ei', JSON.stringify(emailData));
        localStorage.setItem('adt_emsrc', emailParam.emsrc);
      }
    }
    removeEmailAndReplaceHistory(params, emailParam.key, url);
  } else if (hashedEmailParam) {
    const emailData = {
      created: Date.now(),
      value: {
        sha1Hash: '',
        sha256Hash: hashedEmailParam.value,
      },
    };
    localStorage.setItem('adt_ei', JSON.stringify(emailData));
    localStorage.setItem('adt_emsrc', hashedEmailParam.emsrc);
    removeEmailAndReplaceHistory(params, hashedEmailParam.key, url);
  }
};
